import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { SimpleInfoDialog, Spinner } from 'components';
import { IUserContext } from 'context';
import { IApiRequests, useApiRequests, useDialog, useUser } from 'hooks';
import { updateInvite, selectInvite, InviteData, clearInvite } from 'store/invite';
import { selectUser } from 'store/user';
import { useAppDispatch, useAppSelector } from 'store';
import { ApiUser, InvitationInfoResponse } from 'types';
import { isHipaaLink } from '../../config/productProfile';

import invitation from '../../assets/images/invitation.png';
import styles from './InvitationPage.module.scss';

export const InvitationPage = () => {
  const { t } = useTranslation('auth');
  const invite: InviteData | null = useAppSelector(selectInvite);
  const user: ApiUser | null = useAppSelector(selectUser);
  const dispatch: any = useAppDispatch();
  const { invitationDecline, invitationInfo, invitationAccept }: IApiRequests = useApiRequests();
  const navigate = useNavigate();
  const { showDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();
  const { logout }: IUserContext = useUser();

  const search = useLocation().search;
  const updateSearch = search.replace('?userId', '&userId');
  const invitationId: string | null = new URLSearchParams(updateSearch).get('invitationId');
  const userId: string | null = new URLSearchParams(updateSearch).get('userId');

  const [isValid, setIsValid] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!invitationId) {
      setIsValid(false);
      setLoading(false);
      return;
    }

    invitationInfo(invitationId)
      .then((data: InvitationInfoResponse) => {
        setIsValid(data.isValid);
        setLoading(false);
        if (data.isValid) {
          dispatch(
            updateInvite({
              invitationId,
              email: data?.email,
              organizationName: data?.organization,
              firstName: data?.firstName,
              lastName: data?.lastName,
              userId,
            }),
          );

          if (!userId && user) {
            enqueueSnackbar(t('invitationLoggedOutInfo', { email: data?.email }), { variant: 'info' });
            logout();
            return;
          }

          if (userId && userId !== user?.id) {
            enqueueSnackbar(t('invitationPleaseLoginInfo', { email: data?.email }), { variant: 'info' });
            logout();
            navigate('/login');
          }
        }
      })
      .catch((e) => {
        enqueueSnackbar(t('fetchingInvitationInfoError', { message: e.message }), { variant: 'error' });
        navigate('/');
      });
  }, []);

  const handleCancel = (): void => {
    invitationDecline(invitationId!)
      .then(() => {
        dispatch(clearInvite());
        navigate('/');

        const modal = showDialog(SimpleInfoDialog, {
          title: t('declinedTitle'),
          text: t('declinedMessage', { organization: invite!.organizationName }),
          onConfirm: () => {
            modal.destroy();
          },
        });
      })
      .catch((e) => {
        enqueueSnackbar(t('declineInvitationError', { message: e.message }), { variant: 'error' });
      });
  };

  const handleRedirectHomeExpired = (): void => {
    navigate('/');
  };

  const handleAccept = async (): Promise<void> => {
    if (userId && userId === user?.id) {
      await invitationAccept(invitationId!);
    }
    navigate('/registerByInvitation');
  };

  return (
    <>
      {loading && <Spinner />}
      {!loading && (
        <Box className={styles.invitationContainer}>
          <Box>
            <Box className={styles.invitationInnerContainer}>
              <img className={styles.invitationImage} src={invitation} alt='' />
              <Typography className={styles.invitationTitle} variant='body2'>
                {isValid
                  ? t('invitedToJoin', {
                      organization: invite?.organizationName,
                      brand: isHipaaLink ? 'HIPAA LINK' : 'VIA3',
                    })
                  : t('invitationNoLongerValid')}
              </Typography>

              {isValid ? (
                <>
                  <Typography variant='body1'>
                    {t('invitationBody', { organization: invite?.organizationName })}
                  </Typography>
                  <Typography variant='body1'>
                    {t('wouldYouLikeToJoin', { organization: invite?.organizationName })}
                  </Typography>
                  <Box className={styles.buttonsContainer}>
                    <Button className={styles.buttonsContainerItem} variant='outlined' onClick={handleCancel}>
                      {t('decline')}
                    </Button>
                    <Button className={styles.buttonsContainerItem} variant='contained' onClick={handleAccept}>
                      {t('accept')}
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Button
                    className={styles.buttonsContainerItemNoValid}
                    variant='contained'
                    onClick={handleRedirectHomeExpired}
                  >
                    {t('ok')}
                  </Button>
                  <Typography variant='body1'>{t('invitationErrorContactAdmin')}</Typography>
                </>
              )}
            </Box>
            <Box>
              <Typography className={styles.wantToKnowHyppaText}>
                {t('wantKnowMoreBrand', { brand: isHipaaLink ? 'HIPAA LINK' : 'VIA3' })}{' '}
                <a
                  href={isHipaaLink ? 'https://www.hipaalink.net/' : 'https://www.via3.com/'}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {t('clickHere')}
                </a>
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};
